import {
    Avatar,
    Box,
    Flex,
    HStack,
    Skeleton,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import Card from '../../../components/card/Card';
import ErrorState from '../../../components/states/ErrorState';
import { translate } from '../../../i18n';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetRevenuePerPlayer } from '../hooks/useGetRevenuePerPlayer';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';

type Props = {
    startDate?: Date;
    endDate?: Date;
    limit?: number;
};

function formatCurrency(value: number): string {
    return `${value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })} €`;
}

export default function RevenuePerPlayerCard({ startDate, endDate, limit = 10 }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { data, isInitialLoading, isError, refetch } = useGetRevenuePerPlayer(
        selectedLocation?.id,
        startDate,
        endDate,
        limit,
    );
    /**
     * A refused read left `data` undefined, `items` empty, and this card said
     * `impact.no_data` — "No data" — which is a statement about the venue's
     * players rather than about a read that did not happen.
     */
    /**
     * All three states come from ONE classification of the query's own flags
     * (R102-1, closing D62). They used to be three separate derivations, one
     * of which — `isReadPending(Boolean(selectedLocation?.id), …)` — restated
     * this hook's `enabled:` expression in a second file. That restatement is
     * correct today and is the instrument that fails silently when it drifts:
     * a gate re-derived incompletely is wrong in the direction that spins or
     * claims forever, and passes every census, which is the whole of runs
     * 99–103. `isInitialLoading` is a fact the query knows about itself and no
     * caller can restate wrongly.
     *
     * An empty leaderboard is only a fact once the read has ANSWERED: a
     * disabled query reports `isLoading: true` with no request in flight, so
     * keying the empty state on `items.length` alone announced "no data" for a
     * read never issued.
     */
    const readState = classifyRead(isError, isInitialLoading, data);
    const unreadable = readState === 'unreadable';
    const pending = readState === 'loading';
    const answered = readState === 'answered';

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textColorSecondary = 'secondaryGray.600';
    const rowBorderColor = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const rankColor = useColorModeValue('brand.500', 'white');

    const items = data ?? [];

    return (
        <Card alignItems='flex-start' flexDirection='column' w='100%' p='20px'>
            <Flex align='flex-start' w='100%' direction='column' mb='16px'>
                <Text color={textColor} fontSize='xl' fontWeight='700' lineHeight='100%'>
                    {translate('impact.top_players')}
                </Text>
                <Text color={textColorSecondary} fontSize='sm' mt='4px'>
                    {translate('impact.top_players_hint')}
                </Text>
            </Flex>

            {unreadable ? (
                <ErrorState
                    message={translate('common.unexpected_error')}
                    onRetry={() => {
                        refetch();
                    }}
                />
            ) : pending ? (
                <Flex direction='column' w='100%' gap='12px'>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} h='48px' borderRadius='8px' />
                    ))}
                </Flex>
            ) : !answered ? (
                // Run 100's critique called this arm — a bare em dash — ambiguous
                // beside a loading state, and it was: the ONLY state that reaches
                // it is `never-issued`, which does not resolve, so the operator
                // stared at a dash with nothing saying a location was wanted.
                <ScopePrompt gate='location' />
            ) : items.length === 0 ? (
                <Flex py='32px' w='100%' justify='center'>
                    <Text color={textColorSecondary} fontSize='sm'>
                        {translate('impact.no_data')}
                    </Text>
                </Flex>
            ) : (
                <Flex direction='column' w='100%'>
                    {items.map((item, idx) => (
                        <HStack
                            key={item.user.id}
                            py='12px'
                            borderBottomWidth={idx === items.length - 1 ? '0' : '1px'}
                            borderBottomColor={rowBorderColor}
                            spacing='12px'
                        >
                            <Text
                                w='24px'
                                color={rankColor}
                                fontSize='sm'
                                fontWeight='700'
                                textAlign='center'
                            >
                                {idx + 1}
                            </Text>
                            <Avatar
                                size='sm'
                                name={`${item.user.firstName} ${item.user.lastInitial}`}
                            />
                            <Box flex='1'>
                                <Text color={textColor} fontSize='sm' fontWeight='600'>
                                    {item.user.firstName} {item.user.lastInitial}
                                </Text>
                                <Text color={textColorSecondary} fontSize='xs'>
                                    {item.bookingCount} {translate('impact.bookings')}
                                </Text>
                            </Box>
                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                {formatCurrency(item.totalRevenue)}
                            </Text>
                        </HStack>
                    ))}
                </Flex>
            )}
        </Card>
    );
}
